// @flow
import axios from 'axios';
import BaseModel from 'Stores/BaseModel';
import AboutUsModel from 'Stores/AboutUsModel';
import CareerModel from 'Stores/CareerModel';
import {
  isInvalid,
  validateLength,
  validateEmail,
  isValidMail,
  validateUrl,
  validateInternalLink,
  validateImageSelect,
  validateNotNull,
} from 'Utils/Validators';
import AssetModel from 'Stores/AssetModel';
import { stripTags } from 'Utils';

type SocialMedia = {
  instagram: string,
  facebook: string,
  youtube: string,
  twitter: string,
  tiktok: string,
  linkedin: string,
  dealerlink: string,
  dealerlinkTitle: string,
};

type Startpage = {
  title: string,
  text: string,
};

type Notifications = { email: Array<string> };

type Privacy = {
  name: string,
  street: string,
  streetNumber: string,
  city: string,
  postalCode: string,
  email: string,
  text: string,
};

type ContentPrivacy = {
  offersPrivate: boolean,
  offersBusiness: boolean,
  teasers: boolean,
};

type Imprint = {
  privacyText: string,
  text: string,
};

export type BentoboxTile = {
  customEnabled: boolean,
  headline: string,
  headlineLevel: 'h1' | 'h2' | 'h3',
  forceUppercase: boolean,
  image: AssetModel | null,
  consumptionText: string | null,
  consumptionInfo: string | null,
  consumptionInfoCustomDescriptionEnabled: boolean,
  consumptionInfoCustomDescription: string | null,
  url: string,
};

export default class DealerSettingsModel extends BaseModel {
  aboutus: AboutUsModel;

  career: CareerModel;

  startpage: Startpage = {
    title: '',
    text: '',
  };

  privacy: Privacy = {
    name: '',
    street: '',
    streetNumber: '',
    city: '',
    postalCode: '',
    email: '',
    text: '',
  };

  notifications: Notifications = {
    email: [],
  };

  imprint: Imprint = {
    privacyText: '',
    text: '',
  };

  // Whether the dealer wants the page "KFZ Versicherung" visible or not
  showCarInsurance: boolean = true;

  showBrochure: boolean = true;
  showUsedcarValuation: boolean = true;

  // TESTDATA
  isOsbUser: boolean = true;
  isSalesPartner: boolean = false;
  isServicePartner: boolean = false;
  salesPartnerOnly: boolean = false;
  // Whether the dealer is "just" a service partner or a full sales partner
  servicePartnerOnly: boolean = false;
  customTopCars: Object = {};

  socialMedia: SocialMedia = {
    facebook: '',
    instagram: '',
    youtube: '',
    twitter: '',
    tiktok: '',
    linkedin: '',
    dealerlink: '',
    dealerlinkTitle: '',
  };

  onboardingComplete: boolean = false;
  offersOnlineConsultation: boolean = false;
  showAccessoryPrice: boolean = false;
  // in lead forms a different dealer id might be used
  leadsMimicDealerId: number | null;

  bentobox: BentoboxTile = {
    customEnabled: false,
    headline: '',
    headlineLevel: 'h2',
    forceUppercase: true,
    image: null,
    consumptionText: '',
    consumptionInfo: null,
    consumptionInfoCustomDescriptionEnabled: false,
    consumptionInfoCustomDescription: '',
    url: '',
  };

  constructor(data: Object = {}) {
    super();

    this.aboutus = new AboutUsModel(data.aboutus || {});
    this.career = new CareerModel(data.career || this.career || {});
    this.socialMedia = data.socialMedia || this.socialMedia;
    this.startpage = data.startpage || this.startpage;
    this.privacy = data.privacy || this.privacy;
    this.notifications = data.notifications || this.notifications;
    this.imprint = data.imprint || this.imprint;
    this.showBrochure = data.hasOwnProperty('showBrochure') ? data.showBrochure : this.showBrochure;
    this.showUsedcarValuation = data.hasOwnProperty('showUsedcarValuation')
      ? data.showUsedcarValuation
      : this.showUsedcarValuation;
    this.showCarInsurance = data.hasOwnProperty('showCarInsurance')
      ? data.showCarInsurance
      : this.showCarInsurance;
    this.showCarConfigurator = data.showCarConfigurator;
    this.customerAllianceWidget = {
      isActive: data.customerAllianceWidget && data.customerAllianceWidget.isActive,
      url: (data.customerAllianceWidget && data.customerAllianceWidget.url) || null,
      hasId: data.customerAllianceWidget && data.customerAllianceWidget.hasId,
    };
    this.whatsappWidget = {
      isActive: data.whatsappWidget && data.whatsappWidget.isActive,
      whatsappUrl: data.whatsappWidget && data.whatsappWidget.whatsappUrl,
    };
    this.isOsbUser = data.isOsbUser;
    this.isSalesPartner = data.isSalesPartner;
    this.isServicePartner = data.isServicePartner;
    this.salesPartnerOnly = data.isServicePartner === false && data.isSalesPartner === true;
    this.servicePartnerOnly = data.isServicePartner === true && data.isSalesPartner === false;
    this.onboardingComplete = data.onboardingComplete;
    this.offersOnlineConsultation = data.offersOnlineConsultation;
    this.showAccessoryPrice = data.showAccessoryPrice;
    this.customTopCars = data.customTopCars;
    this.leadsMimicDealerId = data.leadsMimicDealerId;
    this.showLocationLink = data.showLocationLink;
    this.bentobox = data.bentobox;
  }

  getSocialMediaLinks = () => {
    const links = [];

    Object.keys(this.socialMedia).forEach((key) => {
      if (
        this.socialMedia[key] &&
        this.socialMedia[key].length > 0 &&
        key !== 'dealerlink' &&
        key !== 'dealerlinkTitle'
      ) {
        links.push({
          key: key,
          href: this.socialMedia[key],
        });
      }
    });

    return links;
  };

  valid = (props) => {
    this.errors = {};

    const { urlType } = props ?? { urlType: this.getBentoboxUrlType() };
    const emailErrors = {};
    const bentoboxErrors = {};

    this.notifications.email.forEach((item, index) => {
      validateLength(emailErrors, 'email', item, 1, 50);
      validateEmail(emailErrors, `email--${index}`, item);
      if (!isValidMail(item)) {
        emailErrors['email'] = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
      }
    });

    if (Object.keys(emailErrors).length > 0) {
      this.errors['notifications'] = emailErrors;
    }

    this.bentobox.headline = this.bentobox.headline.replace(/(\s|&nbsp;)+/g, ' ');
    const plainHeadline = stripTags(this.bentobox.headline);

    validateNotNull(bentoboxErrors, 'url', this.bentobox.url);
    if (urlType === 'internal') {
      validateInternalLink(bentoboxErrors, 'url', this.bentobox.url);
    } else {
      validateUrl(bentoboxErrors, 'url', this.bentobox.url);
    }
    validateImageSelect(bentoboxErrors, 'image', this.bentobox.image);
    validateNotNull(bentoboxErrors, 'headline', plainHeadline);
    validateLength(bentoboxErrors, 'headline', plainHeadline, 3, 65);

    if (Object.keys(bentoboxErrors).length > 0) {
      this.errors['bentobox'] = bentoboxErrors;
    }

    return !isInvalid(this.errors);
  };

  getBentoboxUrlType = () => {
    return /^https?:\/\//.test(this.bentobox.url ?? '') ? 'external' : 'internal'
  }

  save = (settings: Object) => {
    return axios({
      method: 'patch',
      url: '/api/v1/dealers/',
      data: {
        settings: {
          ...settings,
        },
      },
    }).then(() => {
      Object.assign(this, settings);
      return this;
    });
  };
}
